import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Card, CardHeader, CardContent, Link, Avatar, FormControlLabel, Switch, IconButton, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider } from '@mui/material';
import { Key, PersonRemove, Check, Close, Add } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useHasPermission } from '../utils/permissionUtils';
import { getAvatarUrl } from '../utils/avatarUtils';
import { useIsLocked } from '../utils/lockUtils';
import { getRoles } from "../redux/ducks/role";
import { useDispatch, useSelector } from 'react-redux';
import { getAccountMembers, removeAccountMembers, addAccountMembers } from '../redux/ducks/account';
import { updateUserAccountRole } from '../redux/slices/users';
import UserAssignmentDialog from './UserAssignmentDialog';

const RoleEditor = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0, 0, 1),
}))

const StyledLink = styled(Link)(({ theme }) => ({
    color: theme.palette.primary.main,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
        textDecoration: 'underline',
    },
}))

const ClientName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
    color: theme.palette.text.primary,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    lineHeight: 1.3,
}))

const ActiveUser = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    textDecoration: 'none',
    color: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}))

const UserBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
}))

const StyledCard = styled(Card)(({ theme }) => ({
    boxShadow: theme.shadows[3],
}));


const AccountUserBlock = ({ accountId, accountData }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [people, setPeople] = useState([]);
    const [showDisabled, setShowDisabled] = useState(false);
    const hasPermission = useHasPermission();
    const canSeeInternal = hasPermission('canSeeInternal');
    const canEditOwnClient = hasPermission('editOwnClient');
    const canAddAccountMembers = hasPermission('addAccountMembers');
    const isLocked = useIsLocked(accountData, accountData?.client);
    const [isEditing, setIsEditing] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [removingUserId, setRemovingUserId] = useState(null);
    const [editedRole, setEditedRole] = useState(null);
    const [roles, setRoles] = useState([]);
    const ownUser = useSelector(state => state.auth.currentUser._id);
    const [isConfirmingRemoveAccountMember, setIsConfirmingRemoveAccountMember] = useState(false);
    const [isUserAssignmentsDialogOpen, setIsUserAssignmentsDialogOpen] = useState(false);

    const groupedUsers = useMemo(() => {
        const groups = people.reduce((acc, user) => {
            const clientName = user.client?.name || "Unassigned";
            if (!acc[clientName]) {
                acc[clientName] = [];
            }
            acc[clientName].push(user);
            return acc;
        }, {});

        Object.keys(groups).forEach(clientName => {
            groups[clientName].sort((a, b) => {
                if (!a.fullName) return 1;
                if (!b.fullName) return -1;
                return a.fullName.localeCompare(b.fullName);
            });
        });
        return groups;
    }, [people]);


    const handleAddAccountMember = () => {
        setIsUserAssignmentsDialogOpen(true);
    }

    const handleAssignAccountMember = (object, assignees, responsible) => {
        dispatch(addAccountMembers(accountId, assignees.map((assignee) => ({ _id: assignee._id, role: assignee.accountRole })), (data) => {
            assignees = data.members;
            setPeople([...people, ...assignees]);
        }));
        setIsUserAssignmentsDialogOpen(false);
    }

    const handleEditAccountMember = (userId, role) => {
        setEditingUser(userId);
        setEditedRole(role);
        setIsEditing(true);
    }

    const handleUpdateAccountMember = () => {
        dispatch(updateUserAccountRole({ userId: editingUser, accountId, newRole: editedRole }, () => {
            setPeople(people.map(u => u._id === editingUser ? { ...u, accountRole: editedRole } : u));
            setIsEditing(false);
            setEditingUser(null);
            setEditedRole(null);
        }));
    }

    const confirmRemoveAccountMember = (userId) => {
        setRemovingUserId(userId);
        setIsConfirmingRemoveAccountMember(true);
    }

    const handleRemoveAccountMember = () => {
        dispatch(removeAccountMembers(accountId, [{ userId: removingUserId }], () => {
            setPeople(people.filter(u => u._id !== removingUserId));
        }));
        setIsConfirmingRemoveAccountMember(false);
        setRemovingUserId(null);
    }

    useEffect(() => {
        dispatch(
            getAccountMembers(accountId, (result) => {
                setPeople(result);
            })
        );
        dispatch(getRoles((data) => {
            setRoles(data);
        }));
    }, [accountId, dispatch]);

    const handleCloseUserAssignmentsDialog = () => {
        setIsUserAssignmentsDialogOpen(false);
    }

    

    return (
        <>
            <StyledCard>
                <CardHeader
                    title={`Members (${showDisabled ? people?.length : people?.filter((user) => user.active).length || 0})`}
                    action={
                        <Box display="flex" alignItems="center" gap={1}>
                            {canSeeInternal && (
                                <FormControlLabel
                                    control={<Switch size="small" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />}
                                    label="Show Disabled"
                                />
                            )}
                            {canAddAccountMembers && (
                                <Button variant="contained" color="primary" size="small" onClick={handleAddAccountMember} disabled={isLocked}>
                                    <Add fontSize="small" /> Add Member
                                </Button>
                            )}
                        </Box>
                    }
                />
                <CardContent>
                    <Box>
                        {Object.keys(groupedUsers).map((clientName) => (
                            <Box key={clientName}>
                                <ClientName>{clientName}</ClientName>
                                <Divider />
                                {groupedUsers[clientName] !== undefined &&
                                    groupedUsers[clientName]
                                        .filter((user) => (showDisabled ? true : user.active))
                                        .map((user) => (
                                            <ActiveUser key={user._id}>
                                                <UserBox>
                                                    <Box display="flex" alignItems="center">
                                                        <Avatar src={getAvatarUrl(user)} alt={user.fullName}>
                                                            {user.first_name.charAt(0)}
                                                            {user.last_name.charAt(0)}
                                                        </Avatar>
                                                        <Box ml={2}>
                                                            <StyledLink onClick={() => navigate(`/user/${user._id}`)} fontWeight="medium">
                                                                {user.fullName}
                                                            </StyledLink>
                                                            {isEditing && editingUser === user._id && !isLocked ? (
                                                                <RoleEditor>
                                                                    <TextField select size="small" value={editedRole} onChange={(e) => setEditedRole(e.target.value)}>
                                                                        {roles
                                                                            .filter(
                                                                                (role) => role.level >= 3 && (role.type === "General" || (role.type === "Internal" && user.client?.isInternal && canSeeInternal))
                                                                            )
                                                                            .map((role) => (
                                                                                <MenuItem key={role._id} value={role.name}>
                                                                                    {role.name}
                                                                                </MenuItem>
                                                                            ))}
                                                                    </TextField>
                                                                    <IconButton onClick={() => handleUpdateAccountMember()}>
                                                                        <Check />
                                                                    </IconButton>
                                                                    <IconButton onClick={() => setIsEditing(false)}>
                                                                        <Close />
                                                                    </IconButton>
                                                                </RoleEditor>
                                                            ) : (
                                                                <Typography variant="body2" color="textSecondary">
                                                                    {user.accountRole}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                    {(canAddAccountMembers || canEditOwnClient) && !isLocked && (
                                                        <Box>
                                                            <IconButton onClick={() => handleEditAccountMember(user._id, user.accountRole)}>
                                                                <Key />
                                                            </IconButton>
                                                            <IconButton onClick={() => confirmRemoveAccountMember(user._id)} disabled={ownUser === user._id}>
                                                                <PersonRemove />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </UserBox>
                                            </ActiveUser>
                                        ))}
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </StyledCard>
            <Dialog open={isConfirmingRemoveAccountMember} onClose={() => setIsConfirmingRemoveAccountMember(false)}>
                <DialogTitle>Remove Account Member</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to remove {people.find(user => user._id === removingUserId)?.fullName} from the account?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="error" onClick={handleRemoveAccountMember}>Remove</Button>
                </DialogActions>
            </Dialog>
            <UserAssignmentDialog
                isOpen={isUserAssignmentsDialogOpen}
                onClose={handleCloseUserAssignmentsDialog}
                objectId={null}
                objectType="account"
                onAssign={handleAssignAccountMember}
                isNewObject={true}
                accountId={accountId}
                useRoles={true}
                maxRoleLevel={3}
                globalUsers={true}
            />
        </>
    );
};

export default AccountUserBlock;